/**
 * Phase 6 extra — <RankBadge> — premium SVG tier badges.
 *
 *   <RankBadge tier="grandmaster" size="md" animated showLabel />
 *
 * Tiers: iron / bronze / silver / gold / platinum / diamond / master /
 *        grandmaster. Each tier renders a distinct SVG with progressively
 *        more luxurious treatments (gold halo → diamond facets → master
 *        crown+particles → grandmaster flame+gold-swords).
 *
 *   - Animations are CSS-only. Respects `prefers-reduced-motion`.
 *   - sizes: sm=24, md=40, lg=72, xl=128. Internal artwork scales via the
 *     SVG viewBox (0 0 100 100) so a single set of paths covers all sizes.
 *   - showLabel renders the tier name beneath in the tier's accent color.
 *   - role="img" + aria-label for screen readers.
 */
import React from "react";

const SIZES = { sm: 24, md: 40, lg: 72, xl: 128 };
const VIEW = 100;

// ──────────────────────────────────────────────────────────────────────────
// Tier palette — single source of truth, also used by the /ranks page.
// ──────────────────────────────────────────────────────────────────────────
export const TIER_META = {
  iron:        { label: "Iron",        primary: "#5C5F62", secondary: "#3A3D40", accent: "#9CA3AF",
                  band: [   0,  799], flavor: "Every legend starts somewhere.",     distribution: 25 },
  bronze:      { label: "Bronze",      primary: "#C97C3A", secondary: "#7A4520", accent: "#F4B57D",
                  band: [ 800,  999], flavor: "First steps up the ladder.",        distribution: 30 },
  silver:      { label: "Silver",      primary: "#C9D1D9", secondary: "#7A8590", accent: "#E5E7EB",
                  band: [1000, 1199], flavor: "Your name is being remembered.",    distribution: 25 },
  gold:        { label: "Gold",        primary: "#FFC542", secondary: "#A87800", accent: "#FFDD7A",
                  band: [1200, 1499], flavor: "Consistent. Skilled. Respected.",   distribution: 12 },
  platinum:    { label: "Platinum",    primary: "#A6E5F5", secondary: "#5B9BC1", accent: "#D7F2FB",
                  band: [1500, 1799], flavor: "Top 10% of the world.",             distribution:  5 },
  diamond:     { label: "Diamond",     primary: "#5BD5E8", secondary: "#1B7593", accent: "#A2EAF5",
                  band: [1800, 2099], flavor: "Sharper than the rest.",            distribution: 2.5 },
  master:      { label: "Master",      primary: "#A78BFA", secondary: "#6D28D9", accent: "#DDD6FE",
                  band: [2100, 2399], flavor: "Few have walked this far.",         distribution: 0.4 },
  grandmaster: { label: "Grandmaster", primary: "#FF4D4D", secondary: "#8B0000", accent: "#FFB020",
                  band: [2400, 9999], flavor: "The summit. The legend.",           distribution: 0.1 },
};

export const TIER_ORDER = [
  "iron", "bronze", "silver", "gold", "platinum", "diamond", "master", "grandmaster",
];

// ──────────────────────────────────────────────────────────────────────────
// Shared shield outline — every tier uses this hexagonal silhouette so the
// progression feels like the *same* badge upgrading rather than 8 different
// shapes. Higher tiers layer flourishes on top.
// ──────────────────────────────────────────────────────────────────────────
const SHIELD_PATH =
  "M50 6 L86 22 V52 C86 73 70 88 50 96 C30 88 14 73 14 52 V22 Z";

// Decorative laurels for gold+ tiers.
const LAUREL_LEFT  = "M14 50 Q4 56 8 70 Q14 64 18 68 Q15 60 14 50 Z";
const LAUREL_RIGHT = "M86 50 Q96 56 92 70 Q86 64 82 68 Q85 60 86 50 Z";

// Crown for master/grandmaster.
const CROWN_PATH =
  "M30 22 L36 12 L42 22 L50 8 L58 22 L64 12 L70 22 L66 30 H34 Z";

// Crossed swords for grandmaster.
const SWORD_LEFT  = "M22 60 L60 22 L66 28 L28 66 Z";
const SWORD_RIGHT = "M78 60 L40 22 L34 28 L72 66 Z";

// ──────────────────────────────────────────────────────────────────────────
// Per-tier SVG (returns the inner <defs> + paths; the outer <svg> wrapper
// is shared).
// ──────────────────────────────────────────────────────────────────────────
function TierArt({ tier, uid, animated }) {
  const t = TIER_META[tier] || TIER_META.iron;
  const g = (suffix) => `${uid}-${suffix}`;

  const defs = (
    <defs>
      <linearGradient id={g("fill")} x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%"  stopColor={t.accent} />
        <stop offset="55%" stopColor={t.primary} />
        <stop offset="100%" stopColor={t.secondary} />
      </linearGradient>
      <linearGradient id={g("rim")} x1="0" y1="0" x2="1" y2="1">
        <stop offset="0%"  stopColor={t.accent} stopOpacity="0.9" />
        <stop offset="100%" stopColor={t.secondary} stopOpacity="0.4" />
      </linearGradient>
      <radialGradient id={g("glow")} cx="50%" cy="48%" r="60%">
        <stop offset="0%"  stopColor={t.accent} stopOpacity="0.4" />
        <stop offset="100%" stopColor={t.primary} stopOpacity="0" />
      </radialGradient>
      <linearGradient id={g("shine")} x1="0" y1="0" x2="1" y2="0">
        <stop offset="0%"  stopColor="#FFFFFF" stopOpacity="0" />
        <stop offset="50%" stopColor="#FFFFFF" stopOpacity="0.55" />
        <stop offset="100%" stopColor="#FFFFFF" stopOpacity="0" />
      </linearGradient>
    </defs>
  );

  // Base shield (matte for iron, glossy for bronze+).
  const base = (
    <>
      <path d={SHIELD_PATH} fill={`url(#${g("fill")})`} stroke={`url(#${g("rim")})`} strokeWidth="1.5" />
      {tier !== "iron" && (
        <path d={SHIELD_PATH} fill={`url(#${g("glow")})`} opacity="0.9" />
      )}
    </>
  );

  // Inner letter mark — first letter of the tier rendered as a sigil.
  const sigil = (
    <text
      x="50" y={tier === "master" || tier === "grandmaster" ? 70 : 62}
      textAnchor="middle"
      fontFamily="Inter, system-ui, sans-serif"
      fontWeight="900"
      fontSize="32"
      fill={tier === "iron" ? t.accent : tier === "silver" ? "#1f2937" : "#0b0b0b"}
      style={{ filter: tier === "gold" || tier === "grandmaster" ? "drop-shadow(0 1px 0 rgba(0,0,0,0.4))" : undefined }}
    >
      {t.label[0]}
    </text>
  );

  // ── Per-tier embellishments ─────────────────────────────────────────────
  const flourishes = (() => {
    switch (tier) {
      case "iron":
        return null; // flat / matte
      case "bronze":
        return (
          <path d="M30 30 Q50 22 70 30" fill="none" stroke={t.accent} strokeWidth="1.2" opacity="0.55" />
        );
      case "silver":
        return (
          <path d={SHIELD_PATH} fill={`url(#${g("shine")})`} opacity="0.5" />
        );
      case "gold":
        return (
          <>
            <path d={LAUREL_LEFT}  fill={t.secondary} opacity="0.85" />
            <path d={LAUREL_RIGHT} fill={t.secondary} opacity="0.85" />
            <path d="M30 28 Q50 18 70 28" fill="none" stroke={t.accent} strokeWidth="1.5" />
          </>
        );
      case "platinum":
        return (
          <>
            <path d={LAUREL_LEFT}  fill={t.secondary} opacity="0.55" />
            <path d={LAUREL_RIGHT} fill={t.secondary} opacity="0.55" />
            {/* shimmer sweep — a tilted bright bar that animates left→right */}
            <g className={animated ? "rb-shimmer" : ""} style={{ mixBlendMode: "screen" }}>
              <rect x="-40" y="0" width="40" height="100" fill={`url(#${g("shine")})`} transform="skewX(-22)" />
            </g>
          </>
        );
      case "diamond":
        return (
          <>
            {/* faceted gem — geometric polygons over the shield. */}
            <g opacity="0.85">
              <polygon points="50,22 70,40 60,62 40,62 30,40" fill={t.accent} opacity="0.7" />
              <polygon points="50,22 70,40 60,42 40,42 30,40" fill="#FFFFFF" opacity="0.45" />
              <polygon points="40,42 60,42 60,62 40,62" fill={t.primary} opacity="0.6" />
              <polygon points="40,62 60,62 50,78"        fill={t.secondary} opacity="0.95" />
            </g>
            {/* sparkles — 3 dots animated */}
            <g className={animated ? "rb-sparkle" : ""}>
              <circle cx="30" cy="30" r="1.6" fill="#FFFFFF" />
              <circle cx="74" cy="38" r="1.2" fill="#FFFFFF" style={{ animationDelay: "0.7s" }} />
              <circle cx="50" cy="86" r="1.4" fill="#FFFFFF" style={{ animationDelay: "1.3s" }} />
            </g>
          </>
        );
      case "master":
        return (
          <>
            {/* crown on top */}
            <path d={CROWN_PATH} fill={t.accent} stroke={t.secondary} strokeWidth="1.2" />
            <circle cx="50" cy="14" r="2" fill="#FFFFFF" />
            {/* sigil shifted down, see above */}
            {/* particle trail — 3 small circles drifting upward */}
            <g className={animated ? "rb-particles" : ""}>
              <circle cx="35" cy="80" r="1.4" fill={t.accent} />
              <circle cx="50" cy="84" r="1.2" fill={t.accent} style={{ animationDelay: "0.6s" }} />
              <circle cx="65" cy="78" r="1.4" fill={t.accent} style={{ animationDelay: "1.2s" }} />
            </g>
          </>
        );
      case "grandmaster":
        return (
          <>
            {/* flame aura behind the shield (rendered via outer svg too) */}
            {/* gold crown */}
            <path d={CROWN_PATH} fill={t.accent} stroke="#7a4a00" strokeWidth="1.2" />
            <circle cx="50" cy="14" r="2.2" fill="#FFF7E0" />
            {/* crossed swords */}
            <path d={SWORD_LEFT}  fill={t.accent} stroke="#7a4a00" strokeWidth="0.6" opacity="0.95" />
            <path d={SWORD_RIGHT} fill={t.accent} stroke="#7a4a00" strokeWidth="0.6" opacity="0.95" />
            <circle cx="50" cy="44" r="3" fill={t.primary} />
            {/* gem center */}
            <polygon points="50,40 54,46 50,52 46,46" fill="#FFFFFF" opacity="0.7" />
          </>
        );
      default:
        return null;
    }
  })();

  // For grandmaster we also draw an outer flame ring outside the shield.
  const auraRing = tier === "grandmaster" ? (
    <g className={animated ? "rb-flame" : ""}>
      <circle cx="50" cy="52" r="46" fill="none" stroke={t.primary} strokeWidth="2.5" opacity="0.55"
              strokeDasharray="3 4" />
      <circle cx="50" cy="52" r="49" fill="none" stroke={t.accent}  strokeWidth="1.5" opacity="0.6"
              strokeDasharray="2 6" />
    </g>
  ) : tier === "master" ? (
    <g className={animated ? "rb-aura" : ""}>
      <circle cx="50" cy="52" r="47" fill="none" stroke={t.primary} strokeWidth="1.8" opacity="0.45"
              strokeDasharray="2 3" />
    </g>
  ) : null;

  return (
    <>
      {defs}
      {auraRing}
      {base}
      {/* Sigil is rendered BEFORE diamond/master/grandmaster overlays so the
         decorative art lays on top. For lower tiers we want the letter on top. */}
      {!["diamond", "master", "grandmaster"].includes(tier) && flourishes}
      {!["diamond", "master", "grandmaster"].includes(tier) && sigil}
      {["diamond", "master", "grandmaster"].includes(tier) && flourishes}
      {(tier === "master" || tier === "grandmaster") && sigil}
    </>
  );
}

// ──────────────────────────────────────────────────────────────────────────
// CSS — injected once.
// ──────────────────────────────────────────────────────────────────────────
const STYLE_ID = "rank-badge-styles";
function ensureStyles() {
  if (typeof document === "undefined") return;
  if (document.getElementById(STYLE_ID)) return;
  const s = document.createElement("style");
  s.id = STYLE_ID;
  s.textContent = `
    .rb-wrap { display: inline-flex; flex-direction: column; align-items: center; gap: 6px; }
    .rb-wrap svg { display: block; }
    .rb-glow-gold        { filter: drop-shadow(0 0 6px rgba(255,197,66,0.55)); }
    .rb-glow-platinum    { filter: drop-shadow(0 0 5px rgba(166,229,245,0.45)); }
    .rb-glow-diamond     { filter: drop-shadow(0 0 7px rgba(91,213,232,0.55)); }
    .rb-glow-master      { filter: drop-shadow(0 0 9px rgba(167,139,250,0.65)); }
    .rb-glow-grandmaster { filter: drop-shadow(0 0 12px rgba(255,77,77,0.7)) drop-shadow(0 0 4px rgba(255,176,32,0.55)); }
    @keyframes rb-shimmer-keys  { 0% { transform: translateX(0); }   100% { transform: translateX(140px); } }
    @keyframes rb-sparkle-keys  { 0%,100% { opacity: 0.15; }         50%  { opacity: 1; } }
    @keyframes rb-particles-keys{ 0% { transform: translateY(0); opacity: 0.9; } 100% { transform: translateY(-30px); opacity: 0; } }
    @keyframes rb-flame-keys    { 0% { transform: rotate(0deg); }    100% { transform: rotate(360deg); } }
    @keyframes rb-aura-keys     { 0% { transform: rotate(0deg); }    100% { transform: rotate(-360deg); } }
    .rb-shimmer   { animation: rb-shimmer-keys 3.6s linear infinite; }
    .rb-sparkle circle { animation: rb-sparkle-keys 2s ease-in-out infinite; }
    .rb-particles circle { animation: rb-particles-keys 2.4s ease-out infinite; }
    .rb-flame  { transform-origin: 50px 52px; animation: rb-flame-keys 14s linear infinite; }
    .rb-aura   { transform-origin: 50px 52px; animation: rb-aura-keys 18s linear infinite; }
    @media (prefers-reduced-motion: reduce) {
      .rb-shimmer, .rb-sparkle circle, .rb-particles circle, .rb-flame, .rb-aura { animation: none !important; }
    }
  `;
  document.head.appendChild(s);
}

// ──────────────────────────────────────────────────────────────────────────
// Public component
// ──────────────────────────────────────────────────────────────────────────
let _uid = 0;
export function RankBadge({
  tier = "iron",
  size = "md",
  animated = true,
  showLabel = false,
  className = "",
  title,
}) {
  ensureStyles();
  const t = TIER_META[tier] || TIER_META.iron;
  const px = SIZES[size] || SIZES.md;
  // Stable per-instance id so multiple badges don't clash on gradient ids.
  const uidRef = React.useRef(null);
  if (uidRef.current === null) {
    _uid += 1;
    uidRef.current = `rb${_uid}`;
  }
  const glowClass = {
    gold: "rb-glow-gold", platinum: "rb-glow-platinum", diamond: "rb-glow-diamond",
    master: "rb-glow-master", grandmaster: "rb-glow-grandmaster",
  }[tier] || "";

  return (
    <span
      className={`rb-wrap ${className}`}
      data-testid={`rank-badge-${tier}`}
      data-tier-band={`${t.band[0]}-${t.band[1]}`}
      title={title || `${t.label} tier`}
    >
      <svg
        role="img"
        aria-label={`${t.label} tier badge`}
        width={px}
        height={px}
        viewBox={`0 0 ${VIEW} ${VIEW}`}
        className={glowClass}
        overflow="visible"
      >
        <TierArt tier={tier} uid={uidRef.current} animated={animated} />
      </svg>
      {showLabel && (
        <span
          className="text-[10px] uppercase tracking-[0.25em] font-black"
          style={{ color: t.primary }}
        >
          {t.label}
        </span>
      )}
    </span>
  );
}

export default RankBadge;
